/**
 * Partner attribution carrier — Wave 8 step 1 (design §3.2), rides Build B.
 *
 * ONE invisible check-in per install, before auth:
 *   Android → the Play Install Referrer carries an opaque click id (exact).
 *   iPhone  → no clipboard by default; the server matches the first open to a
 *             recent click on our own site (hashed address + coarse device facts).
 *             Only when the server says a tag's dark switch is on does the app
 *             peek the pasteboard — `hasUrlAsync` (no alert) then `getUrlAsync`
 *             (Apple's one alert) — and keeps only `https://coachcedar.com/c/<id>`.
 *   Web     → nothing; web keeps its UTM path.
 *
 * What is stored: a durable partner record {install_id, partner_click_id,
 * capture_method} that EVERY native sign-in merges into the /api/auth0/verify
 * body as `attribution.partner` (UnifiedAuth0Provider — redirect, Apple and
 * Google; 2026-09-23: before that only CallbackScreen.native merged it, and
 * that screen's body is not one the endpoint accepts, so no app signup ever
 * carried it). The server binds signup to the click ONCE (first source
 * sticks — P3) and can resolve from the install id alone, so an aged-out UTM
 * record never loses a partner lead.
 *
 * The record is written BEFORE the check-in posts (install id + any token the
 * phone already read), and a sign-in with no record at all still sends the
 * install id (readPartnerRecordForSignup). A signup that beats the check-in —
 * which can take up to the boot fetch timeout, while a sign-in waits 2 s — is
 * then remembered by the server and bound when the check-in's match arrives.
 *
 * A locally read token (the Play referrer, or an iPhone pasteboard token read
 * after the server asked) is kept in the record even when the check-in fails
 * or the server does not confirm it: it is the phone's only copy. The phone
 * cannot tell a refusal from a rate limit (both answer
 * `{enabled: true, click_id: null}`), so the rule lives on the server:
 * `bind_signup` re-validates the token — the click must exist, the account
 * must post-date it, and no other install or account may hold it — and claims
 * the click for this install in the same transaction (one click, one account).
 *
 * A check-in the server never ANSWERED (offline, non-2xx, dead socket) is not
 * final: it runs again on a later launch, at most MAX_CHECKIN_ATTEMPTS times in
 * all, re-sending any token an earlier attempt read. The iPhone's primary path
 * is the server-side match, which needs the check-in itself. The server relies
 * on this: a check-in it could not finish for a reason a later attempt can
 * outlive — a transient failure of its own, or a signup's late bind that did not
 * land — is answered 503, never an ordinary 2xx, so the phone asks again and the
 * re-sent check-in finishes it (record_install, 2026-09-24). A failure that
 * would repeat on every attempt is answered as an ordinary 2xx, as before.
 * A second pass is not re-sent (the first answer decides finality). The
 * server's nightly reconcile finishes one only when the server recorded its
 * click and the signup's late bind failed after that; a second pass whose own
 * write failed is not recovered (the server drops its pairing after 20 min).
 *
 * Every step fails silent: nothing here may ever delay or block first launch.
 * The tag never reaches the phone — only the opaque `c_…` id (H7).
 */
import { NativeModules, Platform } from 'react-native';
import { storage } from './storage';
import { getApiBaseUrl } from '@/utils/apiConfig';
import { withTimeout, BODY_READ_TIMEOUT_MS } from './requestTimeout';
import { fetchAuthBounded } from '../auth/authBoundedFetch';

export const CHECKED_KEY = 'partner_attr_checked_v1';
export const ATTEMPTS_KEY = 'partner_attr_attempts_v1';
export const RECORD_KEY = 'partner_attr_record_v1';
export const INSTALL_ID_KEY = 'partner_install_id_v1';
export const CLIPBOARD_PREFIX = 'https://coachcedar.com/c/';
export const CLICK_ID_RE = /^c_[0-9A-Za-z]{22}$/;
export const INSTALL_ENDPOINT = '/api/public/partner-install';

export type CaptureMethod = 'ios_match' | 'android_referrer' | 'ios_clipboard' | 'deep_link';
export type CaptureOutcome = 'found' | 'none' | 'skipped' | 'error';

export interface PartnerRecord {
  install_id: string;
  partner_click_id: string | null;
  capture_method: CaptureMethod | null;
  signup_platform: string;
  captured_at: number;
}

export interface InstallCheckIn {
  install_id: string;
  platform: 'ios' | 'android';
  os_version: string;
  device_family: string | null;
  locale: string | null;
  tz: string | null;
  click_id?: string;
  capture_method?: CaptureMethod;
}

// ── pure ──────────────────────────────────────────────────────────────────

export function isClickId(v: unknown): v is string {
  return typeof v === 'string' && CLICK_ID_RE.test(v);
}

/** `https://coachcedar.com/c/<id>` → `<id>`; anything else → null. */
export function parseClipboardToken(url: string | null | undefined): string | null {
  if (!url || typeof url !== 'string') return null;
  const s = url.trim();
  if (!s.startsWith(CLIPBOARD_PREFIX)) return null;
  const id = s.slice(CLIPBOARD_PREFIX.length).split(/[?#/]/)[0];
  return isClickId(id) ? id : null;
}

/** Play Install Referrer `cedar_click=c_…&utm_source=…` → the click id, or null. */
export function extractReferrerClickId(referrer: string | null | undefined): string | null {
  if (!referrer || typeof referrer !== 'string') return null;
  for (const part of referrer.split('&')) {
    const [k, v] = part.split('=');
    if (k?.trim() === 'cedar_click' && isClickId((v ?? '').trim())) return (v ?? '').trim();
  }
  return null;
}

/** Merge the partner record into the signup attribution body. Never overwrites a
 *  partner click id that is already there; returns the input untouched when there
 *  is no record. */
export function mergePartnerIntoAttribution(
  attribution: Record<string, unknown> | undefined,
  record: PartnerRecord | null | undefined,
): Record<string, unknown> | undefined {
  if (!record) return attribution;
  const existing = (attribution?.partner ?? null) as Record<string, unknown> | null;
  if (existing && isClickId(existing.partner_click_id)) return attribution;
  return {
    ...(attribution ?? {}),
    ...(attribution?.signup_platform ? {} : { signup_platform: record.signup_platform }),
    partner: {
      install_id: record.install_id,
      partner_click_id: record.partner_click_id,
      capture_method: record.capture_method,
      signup_platform: record.signup_platform,
    },
  };
}

export function mintInstallId(): string {
  // UUID v4 shape. Not a secret — an opaque per-install handle the server validates by shape.
  const hex = '0123456789abcdef';
  let out = '';
  for (let i = 0; i < 36; i++) {
    if (i === 8 || i === 13 || i === 18 || i === 23) out += '-';
    else if (i === 14) out += '4';
    else if (i === 19) out += hex[(Math.floor(Math.random() * 16) & 0x3) | 0x8];
    else out += hex[Math.floor(Math.random() * 16)];
  }
  return out;
}

// ── storage ───────────────────────────────────────────────────────────────

/** This process's install id once known, so every caller — the first-launch check-in
 *  and a sign-in racing it — names the SAME install even if storage could not keep it. */
let knownInstallId: string | null = null;
/** A mint in progress (single flight): two first calls at once must not mint twice. */
let mintingInstallId: Promise<string> | null = null;

export async function getOrCreateInstallId(): Promise<string> {
  try {
    const existing = await storage.getItem(INSTALL_ID_KEY);
    if (existing && /^[0-9a-fA-F-]{36}$/.test(existing)) {
      knownInstallId = existing;
      return existing;
    }
  } catch { /* fall through */ }
  if (knownInstallId) {
    const id = knownInstallId;
    try { await storage.setItem(INSTALL_ID_KEY, id); } catch { /* best effort */ }
    return id;
  }
  if (!mintingInstallId) {
    mintingInstallId = (async () => {
      const id = mintInstallId();
      knownInstallId = id;
      try { await storage.setItem(INSTALL_ID_KEY, id); } catch { /* best effort */ }
      return id;
    })().finally(() => { mintingInstallId = null; });
  }
  return mintingInstallId;
}

export async function readPartnerRecord(): Promise<PartnerRecord | null> {
  try {
    const raw = await storage.getItem(RECORD_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PartnerRecord;
    return parsed && typeof parsed.install_id === 'string' ? parsed : null;
  } catch {
    return null;
  }
}

async function writePartnerRecord(record: PartnerRecord): Promise<void> {
  try { await storage.setItem(RECORD_KEY, JSON.stringify(record)); } catch { /* best effort */ }
}

/**
 * The record a native sign-in carries as `attribution.partner` (UnifiedAuth0Provider).
 * It ALWAYS names this install: the stored record when there is one, else the install
 * id alone — the same id the first-launch check-in sends (getOrCreateInstallId keeps
 * it). A sign-in can finish before the check-in reaches the server (the check-in may
 * take up to the boot fetch timeout; a sign-in waits for it at most
 * INSTALL_CHECKIN_WAIT_MS). The server then remembers the install for the new account
 * and binds the account when the check-in's match arrives (bind_signup /
 * record_install, migration 1821). Null off native. Never throws.
 */
export async function readPartnerRecordForSignup(): Promise<PartnerRecord | null> {
  if (Platform.OS !== 'ios' && Platform.OS !== 'android') return null;
  try {
    const stored = await readPartnerRecord();
    if (stored) return stored;
    const install_id = await getOrCreateInstallId();
    return {
      install_id,
      partner_click_id: null,
      capture_method: null,
      signup_platform: Platform.OS,
      captured_at: Date.now(),
    };
  } catch {
    return null;
  }
}

// ── device facts (coarse, no permission, no identifiers) ──────────────────

async function deviceFacts(): Promise<Pick<InstallCheckIn, 'os_version' | 'device_family' | 'locale' | 'tz'>> {
  let device_family: string | null = null;
  let locale: string | null = null;
  let tz: string | null = null;
  try {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const Device = require('expo-device');
    device_family = (Device?.modelName as string | null) ?? null;
  } catch { /* optional */ }
  try {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const Localization = require('expo-localization');
    locale = Localization?.getLocales?.()?.[0]?.languageTag ?? null;
    tz = Localization?.getCalendars?.()?.[0]?.timeZone ?? null;
  } catch { /* optional */ }
  if (!tz) {
    try { tz = Intl.DateTimeFormat().resolvedOptions().timeZone ?? null; } catch { /* optional */ }
  }
  return { os_version: String(Platform.Version ?? ''), device_family, locale, tz };
}

// ── native reads ──────────────────────────────────────────────────────────

async function readAndroidReferrer(): Promise<string | null> {
  try {
    const mod = (NativeModules as Record<string, any>)?.CedarAttribution;
    if (!mod || typeof mod.getInstallReferrer !== 'function') return null;
    const raw = await mod.getInstallReferrer();
    return extractReferrerClickId(raw);
  } catch {
    return null;
  }
}

/** The iPhone dark switch. `hasUrlAsync` raises no alert; `getUrlAsync` raises Apple's one. */
async function peekIosClipboard(): Promise<string | null> {
  try {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const Clipboard = require('expo-clipboard');
    if (!Clipboard || typeof Clipboard.hasUrlAsync !== 'function') return null;
    if (!(await Clipboard.hasUrlAsync())) return null;
    const url = await Clipboard.getUrlAsync();
    return parseClipboardToken(url);
  } catch {
    return null;
  }
}

async function postCheckIn(body: InstallCheckIn): Promise<{ enabled: boolean; click_id: string | null; method: CaptureMethod | null; try_clipboard: boolean } | null> {
  // Pre-auth, so not `apiCall` (no token => it returns a synthetic 401 and never
  // sends). fetchAuthBounded is the bounded credential-less boot fetch; on a
  // timeout it logs AUTH_BOOT_FETCH_TIMEOUT site=partner_install in release
  // builds. The body read is bounded separately: fetch resolves on headers.
  const url = `${getApiBaseUrl()}${INSTALL_ENDPOINT}`;
  try {
    const res = await fetchAuthBounded(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }, 'partner_install');
    if (!res.ok) return null;
    return (await withTimeout(res.json(), BODY_READ_TIMEOUT_MS, url)) as any;
  } catch {
    return null;
  }
}

// ── the one call ──────────────────────────────────────────────────────────

/** How long a sign-in waits for a first-launch check-in still on the wire. */
export const INSTALL_CHECKIN_WAIT_MS = 2000;

/** A check-in the server never answered runs again on a later launch, at most this
 *  many attempts in all; then it is marked done like an answered one. */
export const MAX_CHECKIN_ATTEMPTS = 3;

/** The check-in currently on the wire, if any (single flight). */
let inflightCheckIn: Promise<CaptureOutcome> | null = null;

/**
 * Once per install, before auth. Idempotent; never throws; never blocks.
 * Single flight: a second call while one is on the wire shares it (one POST).
 * Final once the server ANSWERS; an unanswered check-in is retried on a later
 * launch (MAX_CHECKIN_ATTEMPTS in all). Returns what happened for logging only.
 */
export async function captureInstallAttributionOnce(): Promise<CaptureOutcome> {
  if (!inflightCheckIn) {
    inflightCheckIn = (async (): Promise<CaptureOutcome> => {
      try {
        if (Platform.OS !== 'ios' && Platform.OS !== 'android') return 'skipped';
        try { if (await storage.getItem(CHECKED_KEY)) return 'skipped'; } catch { /* proceed */ }
        const platform = Platform.OS as 'ios' | 'android';
        const install_id = await getOrCreateInstallId();
        // A token an earlier, unanswered attempt read and kept (an iPhone pasteboard
        // token): re-send it, so a retry neither loses it nor reads the pasteboard again.
        const previous = await readPartnerRecord();
        const sameInstall = previous && previous.install_id === install_id ? previous : null;
        const carried = sameInstall && isClickId(sameInstall.partner_click_id) ? sameInstall : null;
        // RECORD BEFORE THE POST. The post can take up to the boot fetch timeout (~30 s)
        // and a sign-in waits for it at most INSTALL_CHECKIN_WAIT_MS, so a fast signup
        // used to find no record and carry no partner block at all — and nothing bound
        // the account later. The record names this install from here on, so the signup
        // always carries the install id; the server binds the account when this
        // check-in's match lands (bind_signup / record_install, migration 1821).
        if (!sameInstall) {
          await writePartnerRecord({
            install_id, partner_click_id: null, capture_method: null, signup_platform: platform, captured_at: Date.now(),
          });
        }
        let token: string | null = platform === 'android' ? await readAndroidReferrer() : null;
        let method: CaptureMethod | undefined = token ? 'android_referrer' : undefined;
        if (!token && carried) {
          token = carried.partner_click_id;
          method = carried.capture_method ?? undefined;
        }
        // …and any token the phone read itself joins it, still before the post.
        if (token && token !== sameInstall?.partner_click_id) {
          await writePartnerRecord({
            install_id, partner_click_id: token, capture_method: method ?? null, signup_platform: platform, captured_at: Date.now(),
          });
        }
        const facts = await deviceFacts();
        const first = await postCheckIn({ install_id, platform, ...facts, ...(token ? { click_id: token, capture_method: method } : {}) });
        let res = first;
        if (res && res.enabled && !res.click_id && res.try_clipboard && platform === 'ios') {
          const pasted = await peekIosClipboard();
          if (pasted) {
            token = pasted;
            method = 'ios_clipboard';
            res = await postCheckIn({ install_id, platform, ...facts, click_id: token, capture_method: method });
          }
        }
        let attempts = 0;
        try { attempts = Number(await storage.getItem(ATTEMPTS_KEY)) || 0; } catch { /* 0 */ }
        attempts += 1;
        const final = first !== null || attempts >= MAX_CHECKIN_ATTEMPTS;
        try {
          if (final) await storage.setItem(CHECKED_KEY, String(Date.now()));
          else await storage.setItem(ATTEMPTS_KEY, String(attempts));
        } catch { /* best effort */ }
        // The server's answer wins when it confirms a click. When the check-in failed
        // (network, non-2xx, dead socket) or the server did not confirm, the token this
        // phone read itself is still the lead: keep it with the method that read it.
        // bind_signup decides whether it may credit an account.
        const serverClickId = res?.click_id && isClickId(res.click_id) ? res.click_id : null;
        const localClickId = token && isClickId(token) ? token : null;
        const clickId = serverClickId ?? localClickId;
        const record: PartnerRecord = {
          install_id,
          partner_click_id: clickId,
          capture_method: serverClickId
            ? (res?.method ?? method ?? null)
            : (localClickId ? (method ?? null) : null),
          signup_platform: platform,
          captured_at: Date.now(),
        };
        await writePartnerRecord(record);
        if (__DEV__) console.log(`PARTNER_ATTR_CAPTURE outcome=${clickId ? 'found' : 'none'} source=${serverClickId ? 'server' : (localClickId ? 'local' : 'none')} method=${record.capture_method ?? 'null'} answered=${first !== null} attempt=${attempts}${final ? '' : ' retry=next_launch'}`);
        return clickId ? 'found' : 'none';
      } catch (e) {
        if (__DEV__) console.warn('captureInstallAttributionOnce failed', e);
        return 'error';
      }
    })().finally(() => { inflightCheckIn = null; });
  }
  return inflightCheckIn;
}

/**
 * Let a sign-in wait for the first-launch check-in, briefly, so the partner
 * record it writes is there to read. Resolves at once when nothing is in
 * flight; gives up after `timeoutMs`; never throws. A slow network must never
 * hold a sign-in hostage — on a timeout the sign-in proceeds with whatever
 * record exists.
 */
export async function waitForInstallCheckIn(timeoutMs: number = INSTALL_CHECKIN_WAIT_MS): Promise<void> {
  const pending = inflightCheckIn;
  if (!pending) return;
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    await Promise.race([
      pending.then(() => undefined, () => undefined),
      new Promise<void>((resolve) => { timer = setTimeout(resolve, timeoutMs); }),
    ]);
  } catch {
    /* never throws */
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }
}
